import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Mail, Phone, MapPin, Facebook, Clock, Send, Loader2, Calendar, PenLine } from "lucide-react";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";
import { colors, contact, social, externalLinks } from "../config/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORY_PROMPT =
  "I'd like to share my story with Dorsey Taylor Homes.\n\n• My connection (resident, partner, program participant, community member): \n• A moment that stood out to me: \n• What I'd like other families to know: \n\n(Feel free to share as much or as little as you're comfortable with.)";

const ContactPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const topic = params.get("topic");
  const isStoryMode = topic === "story";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: isStoryMode ? STORY_PROMPT : "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Re-prime the textarea if the user lands on /contact?topic=story after mount
  useEffect(() => {
    if (isStoryMode) {
      setFormData((prev) =>
        prev.message && prev.message !== STORY_PROMPT ? prev : { ...prev, message: STORY_PROMPT }
      );
    }
  }, [isStoryMode]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12">
            <p
              className="text-xs uppercase tracking-widest font-semibold mb-3"
              style={{ color: colors.terracotta }}
              data-testid="contact-page-eyebrow"
            >
              {isStoryMode ? "Share Your Story" : "Get In Touch"}
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-5"
              style={{ color: colors.charcoal }}
            >
              {isStoryMode
                ? "Your voice helps the next family."
                : "We'd love to hear from you."}
            </h1>
            <p className="text-lg max-w-2xl" style={{ color: colors.textMuted }}>
              {isStoryMode ? (
                <>
                  Tell us about your experience with Dorsey Taylor Homes — as a resident, program
                  participant, partner, or community member. With your permission, we may feature
                  your story on our website to help others see what stable housing makes possible.
                </>
              ) : (
                <>
                  Whether you&apos;re an individual looking for housing support, an organization
                  interested in partnership, or a community member ready to connect — reach out and
                  our team will get back to you.
                </>
              )}
            </p>
            {isStoryMode && (
              <div
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
                data-testid="story-mode-badge"
              >
                <PenLine className="w-4 h-4" />
                Story Submission Mode
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div className="space-y-5">
              <Card className="border-0 shadow-sm" style={{ backgroundColor: colors.white }}>
                <CardContent className="p-7 space-y-5">
                  <h2 className="text-xl font-bold mb-2" style={{ color: colors.charcoal }}>
                    Contact Information
                  </h2>

                  <a href={contact.phoneHref} className="flex items-start space-x-4 group">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: colors.charcoal }}>Phone</p>
                      <p className="group-hover:underline" style={{ color: colors.textMuted }}>{contact.phone}</p>
                      <p className="text-xs mt-1" style={{ color: colors.textMuted }}>{contact.hours.weekdays}</p>
                    </div>
                  </a>

                  <a href={contact.emailHref} className="flex items-start space-x-4 group">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: colors.terracotta }}
                    >
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: colors.charcoal }}>Email</p>
                      <p className="group-hover:underline break-all" style={{ color: colors.textMuted }}>
                        {contact.email}
                      </p>
                      <p className="text-xs mt-1" style={{ color: colors.textMuted }}>We respond within 24 hours</p>
                    </div>
                  </a>

                  <div className="flex items-start space-x-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: colors.sage }}
                    >
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: colors.charcoal }}>Address</p>
                      <p style={{ color: colors.textMuted }}>{contact.address.line1}</p>
                      <p style={{ color: colors.textMuted }}>{contact.address.line2}</p>
                    </div>
                  </div>

                  <a
                    href={social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start space-x-4 group"
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: colors.gold }}
                    >
                      <Facebook className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: colors.charcoal }}>Social</p>
                      <p className="group-hover:underline" style={{ color: colors.textMuted }}>{social.facebookHandle}</p>
                    </div>
                  </a>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm" style={{ backgroundColor: colors.white }}>
                <CardContent className="p-7">
                  <div className="flex items-center space-x-3 mb-5">
                    <Clock className="w-5 h-5" style={{ color: colors.terracotta }} />
                    <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>Office Hours</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b" style={{ borderColor: "#EDE7DA" }}>
                      <span className="font-medium" style={{ color: colors.charcoal }}>Monday – Friday</span>
                      <span style={{ color: colors.textMuted }}>9 AM – 6 PM</span>
                    </div>
                    <div className="flex justify-between py-2 border-b" style={{ borderColor: "#EDE7DA" }}>
                      <span className="font-medium" style={{ color: colors.charcoal }}>Saturday</span>
                      <span style={{ color: colors.textMuted }}>10 AM – 4 PM</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="font-medium" style={{ color: colors.charcoal }}>Sunday</span>
                      <span style={{ color: colors.textMuted }}>By appointment only</span>
                    </div>
                  </div>

                  <a
                    href={externalLinks.calendly}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-calendly-link"
                    className="inline-flex items-center mt-5 px-5 py-3 rounded-full font-semibold text-sm text-white shadow-sm hover:shadow-md transition-all"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule a Call
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="border-0 shadow-sm" style={{ backgroundColor: colors.white }}>
              <CardContent className="p-7 lg:p-9">
                <h2 className="text-xl font-bold mb-5" style={{ color: colors.charcoal }}>
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {submitStatus === "success" && (
                    <div
                      className="rounded-lg p-4 text-sm font-medium"
                      style={{ backgroundColor: "rgba(135,169,107,0.15)", color: colors.primary }}
                      data-testid="contact-success"
                    >
                      Thank you for your message — we&apos;ll get back to you within 24 hours.
                    </div>
                  )}
                  {submitStatus === "error" && (
                    <div
                      className="rounded-lg p-4 text-sm font-medium"
                      style={{ backgroundColor: "rgba(184,101,74,0.12)", color: colors.terracottaDark }}
                      data-testid="contact-error"
                    >
                      Sorry, there was an error submitting your message. Please try again or call us
                      directly.
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium" style={{ color: colors.charcoal }}>Full Name *</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        placeholder="Your full name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium" style={{ color: colors.charcoal }}>Phone Number</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        placeholder="(123) 456-7890"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" style={{ color: colors.charcoal }}>Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      placeholder="your.email@example.com"
                      data-testid="contact-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" style={{ color: colors.charcoal }}>Message *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      rows={6}
                      placeholder="Tell us how we can help — housing questions, partnership ideas, or feedback."
                      className="resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full text-white py-6 rounded-full font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                    style={{ backgroundColor: colors.primary }}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center" style={{ color: colors.textMuted }}>
                    * Required fields. We&apos;ll respond to your message within 24 hours.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
